// useWebRTC.ts
// Rewritten to align with the DispatcherConsole and UnitRadio components above.
// - Solid audio lifecycle: resume contexts on gestures, create <audio> per-consumer, setSinkId on change
// - Explicit producer→talkgroup mapping so RX lamps & per-talkgroup mute/volume work
// - Prewarmed per-TG recv transports, singletons guarded against dup init
// - Safer consume retry for early CANNOT_CONSUME, and robust cleanup

import { useState, useEffect, useRef, useCallback } from 'react';
import { Socket, io } from 'socket.io-client';
import { SOCKET_URL, SOCKET_PATH } from '../lib/env';
import * as mediasoupClient from 'mediasoup-client';

// ---------- Module-level singletons ----------
let sharedSocket: Socket | null = null;
let sharedDevice: mediasoupClient.Device | null = null;
const recvTransportsByTG = new Map<string, mediasoupClient.types.Transport>();

// Export function to get the radio socket
export const getRadioSocket = () => sharedSocket;
let sendTransportSingleton: mediasoupClient.types.Transport | null = null;

type Consumer = mediasoupClient.types.Consumer;

const consumersByProducer = new Map<string, Consumer>();
const audioByProducer = new Map<string, HTMLAudioElement>();
const metaByProducer = new Map<
  string,
  { talkgroupId: string; userId: string; kind: mediasoupClient.types.MediaKind }
>();

// Fast checks/guards
const creatingRecvFor = new Set<string>();
const subscribingTGs = new Set<string>();
let mounts = 0;
let currentUserId: string | null = null;

// Router+ICE caches
let routerCapsCache: any = null;
let routerCapsPromise: Promise<any> | null = null;
let iceServersCache: RTCIceServer[] = [];

// ---------- Helpers ----------
const resumeAllAudioContexts = async () => {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const tmp = new Ctx();
    if (tmp.state !== 'running') await tmp.resume();
    await tmp.close();
  } catch {}
};

const tryPlay = async (el: HTMLAudioElement) => {
  try {
    console.log('[tryPlay] Attempting to play audio element...');
    await el.play();
    console.log('[tryPlay] Audio element playing successfully');
  } catch (err: any) {
    console.warn('[tryPlay] Play failed:', err.name, err.message);
    // Allow a fast follow-up attempt for minor race/readyState issues.
    if (err?.name !== 'NotAllowedError') {
      setTimeout(() => {
        console.log('[tryPlay] Retrying audio playback...');
        el.play().catch((retryErr) => {
          console.error('[tryPlay] Retry also failed:', retryErr);
        });
      }, 80);
    } else {
      console.error('[tryPlay] NotAllowedError - user interaction required for audio playback');
    }
  }
};

// Helper to record processed TX audio for analysis
const recordStream = (stream: MediaStream, ms = 8000) => {
  try {
    const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm';
    const rec = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 128000 });
    const chunks: BlobPart[] = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = () => {
      const blob = new Blob(chunks, { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'processed_tx.webm';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    };
    rec.start();
    setTimeout(() => rec.state !== 'inactive' && rec.stop(), ms);
  } catch {}
};


// ---------- Types ----------
interface WebRTCConfig {
  serverUrl: string;
  token: string;
}
interface MediaDevice {
  deviceId: string;
  label: string;
  kind: MediaDeviceInfo['kind'];
}
interface AudioTalker {
  producerId: string;
  level?: number;
  userId?: string;
  displayName?: string;
}

interface TransmissionLog {
  id: string;
  talkgroupId: string;
  userId: string;
  displayName: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  type: 'unit' | 'dispatcher' | 'all-call';
  audioBlob?: Blob;
  audioUrl?: string;
  mimeType?: string;
}
interface PTTState {
  isTransmitting: boolean;
  talkgroupId: string | null;
  startTime: number | null;
  allCallProducers?: Map<string, mediasoupClient.types.Producer> | null;
}
interface P25VocoderConfig {
  mode: 'P25_PHASE1' | 'P25_PHASE2' | 'CUSTOM';
  bitDepth: number;
  compressionRatio: number;
  noiseLevel: number;
  frameSize: number;
  spectralBoost: number;
  dropoutRate: number;
}

interface WebRTCState {
  isConnected: boolean;
  isInitialized: boolean;
  user: any | null;
  devices: MediaDevice[];
  selectedInputDevice: string | null;
  selectedOutputDevice: string | null;
  pttState: PTTState;
  activeTalkgroups: Map<string, AudioTalker[]>;
  transmissionLogs: Map<string, TransmissionLog[]>;
  talkgroups?: any[];
  error: string | null;
  txFxEnabled: boolean;
  fxMode: 'off' | 'analog' | 'codec2';
  p25VocoderConfig: P25VocoderConfig;
  talkgroupVolumes: Map<string, number>;
  mutedTalkgroups: Set<string>;
}

// ---------- Hook ----------
export const useWebRTC = (config: WebRTCConfig, talkgroups?: any[], subscriptionCallbacks?: {
  addToCurrentUserSubscriptions?: (talkgroupId: string) => void;
  removeFromCurrentUserSubscriptions?: (talkgroupId: string) => void;
  setCurrentUserChannel?: (talkgroupId: string) => void;
}) => {
  const { serverUrl, token } = config;
  
  // Keep token in ref for upload callbacks
  const tokenRef = useRef<string>(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // P25 Vocoder Presets - Aggressive 3600 bits/s characteristics
  const P25_PRESETS: Record<string, P25VocoderConfig> = {
    P25_PHASE1: {
      mode: 'P25_PHASE1',
      bitDepth: 4,           // Much lower bit depth for 3600 bits/s
      compressionRatio: 12.0, // Very aggressive compression
      noiseLevel: 0.015,     // Higher noise for low bit rate
      frameSize: 80,         // Smaller frames for more artifacts
      spectralBoost: 6.0,    // Strong spectral shaping
      dropoutRate: 0.008     // Higher dropout rate
    },
    P25_PHASE2: {
      mode: 'P25_PHASE2',
      bitDepth: 5,           // Still low bit depth for 3600 bits/s
      compressionRatio: 10.0, // Aggressive compression
      noiseLevel: 0.012,     // Higher noise
      frameSize: 90,         // Small frames
      spectralBoost: 5.5,    // Strong spectral shaping
      dropoutRate: 0.005     // Higher dropout rate
    },
    CUSTOM: {
      mode: 'CUSTOM',
      bitDepth: 4,
      compressionRatio: 11.0,
      noiseLevel: 0.018,
      frameSize: 80,
      spectralBoost: 6.5,
      dropoutRate: 0.010
    }
  };

  const [state, setState] = useState<WebRTCState>({
    isConnected: false,
    isInitialized: false,
    user: null,
    devices: [],
    selectedInputDevice: null,
    selectedOutputDevice: null,
    pttState: { isTransmitting: false, talkgroupId: null, startTime: null },
    activeTalkgroups: new Map(),
    transmissionLogs: new Map(),
    talkgroups: talkgroups || [],
    error: null,
    txFxEnabled: false,
    fxMode: 'codec2', // Default to Codec2 vocoder
    p25VocoderConfig: P25_PRESETS.P25_PHASE2, // Default to Phase 2
    talkgroupVolumes: new Map(),
    mutedTalkgroups: new Set(),
  });

  // keep latest talkgroups in state (used for all-call prewarm)
  useEffect(() => {
    if (talkgroups) {
      setState((p) => {
        // Initialize default volumes for new talkgroups
        const newVolumes = new Map(p.talkgroupVolumes);
        talkgroups.forEach((tg: any) => {
          if (!newVolumes.has(tg.id)) {
            newVolumes.set(tg.id, 80); // Default volume
          }
        });
        
        return { 
          ...p, 
          talkgroups,
          talkgroupVolumes: newVolumes
        };
      });
    }
  }, [talkgroups]);

  // Load TX FX preferences from localStorage
  useEffect(() => {
    const savedFxEnabled = localStorage.getItem('txFxEnabled');
    const savedFxMode = localStorage.getItem('fxMode');
    if (savedFxEnabled != null) {
      setState(p => ({ ...p, txFxEnabled: savedFxEnabled === 'true' }));
    }
    if (savedFxMode && ['off', 'analog', 'codec2'].includes(savedFxMode)) {
      setState(p => ({ ...p, fxMode: savedFxMode as 'off' | 'analog' | 'codec2' }));
    }
  }, []);

  // Save TX FX preferences to localStorage
  useEffect(() => {
    localStorage.setItem('txFxEnabled', String(state.txFxEnabled));
  }, [state.txFxEnabled]);

  useEffect(() => {
    localStorage.setItem('fxMode', state.fxMode);
  }, [state.fxMode]);

  // TX FX setters
  const setTxFxEnabled = (enabled: boolean) => setState(p => ({ ...p, txFxEnabled: enabled }));
  const setFxMode = (mode: 'off' | 'analog' | 'codec2') => setState(p => ({ ...p, fxMode: mode }));

  // P25 Vocoder configuration setters
  const setP25VocoderMode = (mode: 'P25_PHASE1' | 'P25_PHASE2' | 'CUSTOM') => {
    setState(p => ({ 
      ...p, 
      p25VocoderConfig: { ...P25_PRESETS[mode], mode }
    }));
  };

  const setP25VocoderConfig = (config: Partial<P25VocoderConfig>) => {
    setState(p => ({ 
      ...p, 
      p25VocoderConfig: { ...p.p25VocoderConfig, ...config }
    }));
  };

  // ---------- Transmission Logging ----------
  const addTransmissionLog = (talkgroupId: string, userId: string, displayName: string, type: 'unit' | 'dispatcher' | 'all-call', audioStream?: MediaStream) => {
    const logId = `${talkgroupId}-${userId}-${Date.now()}`;
    const newLog: TransmissionLog = {
      id: logId,
      talkgroupId,
      userId,
      displayName,
      startTime: Date.now(),
      type
    };

    console.log('Creating transmission log:', logId, 'Type:', type, 'Has audio stream:', !!audioStream);

    setState(p => {
      const next = new Map(p.transmissionLogs);
      const existingLogs = next.get(talkgroupId) || [];
      // Keep the last 50 logs per talkgroup for transmission history
      const updatedLogs = [...existingLogs, newLog].slice(-50);
      next.set(talkgroupId, updatedLogs);
      return { ...p, transmissionLogs: next };
    });

    // Start audio recording if stream is provided
    if (audioStream) {
      // Determine the MIME type that will be used for recording
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/wav')) {
        mimeType = 'audio/wav';
      }
      
      const logData = {
        talkgroupId,
        displayName,
        type,
        startTime: newLog.startTime,
        mimeType
      };
      startAudioRecording(logId, audioStream, logData);
    }

    return logId;
  };

  const endTransmissionLog = (talkgroupId: string, logId: string, source: string = 'unknown') => {
    console.log('endTransmissionLog called for:', logId, 'from source:', source);
    
    setState(p => {
      const next = new Map(p.transmissionLogs);
      const logs = next.get(talkgroupId) || [];
      const updatedLogs = logs.map(log => {
        if (log.id === logId && !log.endTime) {
          const endTime = Date.now();
          const duration = endTime - log.startTime;
          
          console.log('Ending transmission log:', logId, 'Duration:', duration, 'ms', 'Source:', source);
          
          // Stop audio recording if active (with idempotency guard)
          if (!stoppedSet.current.has(logId)) {
            stoppedSet.current.add(logId);
            const recorder = audioRecordersRef.current.get(logId);
            if (recorder && recorder.state === 'recording') {
              console.log('Stopping MediaRecorder for log:', logId, 'Source:', source);
              recorder.stop();
            }
          }
          
          return {
            ...log,
            endTime,
            duration
          };
        }
        return log;
      });
      next.set(talkgroupId, updatedLogs);
      return { ...p, transmissionLogs: next };
    });
  };

  // Upload recording to server
  const uploadRecordingToServer = async (
    logId: string,
    audioBlob: Blob,
    logData?: {
      talkgroupId: string;
      displayName: string;
      type: string;
      startTime: number;
      endTime?: number;
      duration?: number;
      mimeType?: string;
    }
  ): Promise<string> => {
    try {
      // Skip empty recordings
      if (audioBlob.size === 0) {
        console.warn('Skipping upload of empty recording:', logId);
        return '';
      }

      // Resolve the log + talkgroupId either from logData or state
      let resolved = logData;
      if (!resolved) {
        for (const [tgId, logs] of state.transmissionLogs.entries()) {
          const found = logs.find(l => l.id === logId);
          if (found) {
            resolved = {
              talkgroupId: tgId,
              displayName: found.displayName,
              type: found.type,
              startTime: found.startTime,
              endTime: found.endTime,
              duration: found.duration,
              mimeType: found.mimeType,
            };
            break;
          }
        }
      }
      if (!resolved) throw new Error('Transmission log not found');

      // Get MIME type for the payload
      const mt = resolved.mimeType || audioBlob.type || 'audio/webm';

      // Convert audio blob to base64 for JSON payload
      const toBase64 = (blob: Blob) =>
        new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve((r.result as string).split(',')[1]); // strip data URL prefix
          r.onerror = reject;
          r.readAsDataURL(blob);
        });

      const b64 = await toBase64(audioBlob);

      // Use serverUrl (same host as your mediasoup/socket API)
      const url = `${serverUrl.replace(/\/$/, '')}/api/radio/transmissions/upload`;

      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenRef.current || ''}`,
        },
        body: JSON.stringify({
          transmissionId: logId,
          talkgroupId: resolved.talkgroupId,
          mimeType: mt,
          audioData: b64, // base64 payload
          displayName: resolved.displayName,
          type: resolved.type,
          startTime: resolved.startTime,
          endTime: resolved.endTime,
          duration: resolved.duration,
        }),
      });

      if (!resp.ok) {
        const errText = await resp.text().catch(() => '');
        throw new Error(`Upload failed: ${resp.status} ${resp.statusText} ${errText ? `- ${errText}` : ''}`);
      }

      const result = await resp.json();
      // accept either recordingUrl or url
      return result.recordingUrl || result.url || '';
    } catch (err) {
      console.error('Failed to upload recording:', err);
      throw err;
    }
  };

  // Start audio recording for a transmission
  const startAudioRecording = (logId: string, audioStream: MediaStream, logData?: { talkgroupId: string; displayName: string; type: string; startTime: number; endTime?: number; duration?: number; mimeType?: string }) => {
    try {
      // Try different MIME types in order of preference
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/wav')) {
        mimeType = 'audio/wav';
      }
      
      console.log('Using MIME type for recording:', mimeType);
      
            const recorder = new MediaRecorder(audioStream, { 
              mimeType,
              audioBitsPerSecond: 128000
            });
      
      const chunks: BlobPart[] = [];
      audioChunksRef.current.set(logId, chunks);
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
          console.log('Audio chunk received:', event.data.size, 'bytes');
        }
      };
      
      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
      };
      
      recorder.onstart = () => {
        console.log('MediaRecorder started for log:', logId);
      };
      
      recorder.onstop = async () => {
        console.log('MediaRecorder stopped for log:', logId);
        const audioBlob = new Blob(chunks, { type: mimeType });
        
        try {
          // Use provided logData if available, otherwise try to find in state
          let uploadLogData = logData;
          if (!uploadLogData) {
            // Search through all transmission logs
            for (const [tgId, logs] of state.transmissionLogs.entries()) {
              const foundLog = logs.find(l => l.id === logId);
              if (foundLog) {
                uploadLogData = {
                  talkgroupId: tgId,
                  displayName: foundLog.displayName,
                  type: foundLog.type,
                  startTime: foundLog.startTime,
                  endTime: foundLog.endTime,
                  duration: foundLog.duration,
                  mimeType: mimeType
                };
                break;
              }
            }
          } else {
            // Add mimeType to provided logData
            uploadLogData.mimeType = mimeType;
          }
          
          const recordingUrl = await uploadRecordingToServer(logId, audioBlob, uploadLogData);
          
          // Update the log with server URL
          setState(p => {
            const next = new Map(p.transmissionLogs);
            next.forEach((logs, talkgroupId) => {
              const updatedLogs = logs.map(log => {
                if (log.id === logId) {
                  return { ...log, audioUrl: recordingUrl };
                }
                return log;
              });
              next.set(talkgroupId, updatedLogs);
            });
            return { ...p, transmissionLogs: next };
          });
          
          // Clean up pending log entry after successful upload
          if (uploadLogData) {
            // Try to find the composite key for this log
            for (const [k, storedLogId] of pendingLogRef.current) {
              if (storedLogId === logId) {
                pendingLogRef.current.delete(k);
                console.log('Cleaned up pending log after upload:', k);
                break;
              }
            }
          }
          
        } catch (error) {
          console.error('Failed to upload recording:', error);
          // Fallback to local URL if upload fails
          const audioUrl = URL.createObjectURL(audioBlob);
          setState(p => {
            const next = new Map(p.transmissionLogs);
            next.forEach((logs, talkgroupId) => {
              const updatedLogs = logs.map(log => {
                if (log.id === logId) {
                  return { ...log, audioUrl };
                }
                return log;
              });
              next.set(talkgroupId, updatedLogs);
            });
            return { ...p, transmissionLogs: next };
          });
          
          // Clean up local URL after 10 minutes
          setTimeout(() => {
            if (audioUrl) {
              URL.revokeObjectURL(audioUrl);
            }
          }, 10 * 60 * 1000);
        }
      };
      
            recorder.start(1000); // 1s chunks to reduce memory pressure
      audioRecordersRef.current.set(logId, recorder);
    } catch (error) {
      console.warn('Failed to start audio recording:', error);
    }
  };

  // Play audio from transmission log
  const playTransmissionAudio = (logId: string) => {
    const logs = Array.from(state.transmissionLogs.values()).flat();
    const log = logs.find(l => l.id === logId);
    
    if (log && log.audioUrl) {
      console.log('Attempting to play audio for log:', logId, 'URL:', log.audioUrl);
      
      const audio = new Audio();
      
      // Add error event listeners for better debugging
      audio.addEventListener('error', (e) => {
        console.error('Audio playback error:', e);
        console.error('Audio error details:', {
          error: audio.error,
          networkState: audio.networkState,
          readyState: audio.readyState,
          src: audio.src
        });
      });
      
      audio.addEventListener('loadstart', () => {
        console.log('Audio loading started');
      });
      
      audio.addEventListener('canplay', () => {
        console.log('Audio can start playing');
      });
      
            audio.addEventListener('loadedmetadata', () => {
              console.log('Audio metadata loaded:', {
                duration: audio.duration,
                currentTime: audio.currentTime
              });
              
              // Fix Infinity duration for chunked/streamed audio
              if (!isFinite(audio.duration)) {
                audio.currentTime = Number.MAX_SAFE_INTEGER; // seek to end forces finite duration calc
                audio.currentTime = 0;
              }
            });
      
      // Apply output routing to playback
      const outId = state.selectedOutputDevice;
      if (outId && (audio as any).setSinkId) {
        (audio as any).setSinkId(outId).catch((error: any) => {
          if (error.name === 'SecurityError') {
            console.warn('setSinkId requires secure origin (HTTPS)');
          }
        });
      }

      // Set the source and try to play
      audio.src = log.audioUrl;
      audio.play().catch(error => {
        console.error('Failed to play audio:', error);
        console.error('Audio details:', {
          src: audio.src,
          error: audio.error,
          networkState: audio.networkState,
          readyState: audio.readyState
        });
      });
    } else {
      console.warn('No audio URL found for log:', logId);
    }
  };

  // refs to stable objects
  const socketRef = useRef<Socket | null>(null);
  const deviceRef = useRef<mediasoupClient.Device | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const singleProducerRef = useRef<mediasoupClient.types.Producer | null>(null);
  
  // Audio recording refs for 10-minute cache
  const audioRecordersRef = useRef<Map<string, MediaRecorder>>(new Map()); // logId -> MediaRecorder
  const audioChunksRef = useRef<Map<string, BlobPart[]>>(new Map()); // logId -> audio chunks
  
  // Refs to avoid stale closures in socket handlers
  const pttStateRef = useRef<PTTState>({ isTransmitting: false, talkgroupId: null, startTime: null, allCallProducers: null });
  const userRef = useRef<any>(null);
  
  // Refs for latest talkgroup settings (avoid stale state in socket callbacks)
  const talkgroupVolumesRef = useRef<Map<string, number>>(new Map());
  const mutedTalkgroupsRef = useRef<Set<string>>(new Set());
  const selectedOutputDeviceRef = useRef<string | null>(null);
  
  // Composite key helper for handling overlapping unit talkers
  const keyFor = (tg: string, producerId?: string) =>
    producerId ? `${tg}::${producerId}` : tg;
  
  const normalizeName = (s?: string) =>
    s?.trim() || 'Unknown Unit';
  
  // Active and pending log maps using composite keys
  const activeLogRef = useRef<Map<string, string>>(new Map()); // compositeKey -> logId
  const pendingLogRef = useRef<Map<string, string>>(new Map()); // compositeKey -> logId
  
  // Idempotency guard to prevent double-stop of recorders
  const stoppedSet = useRef<Set<string>>(new Set()); // logIds that have been stopped
  
  // Keep refs in sync with state
  useEffect(() => {
    pttStateRef.current = state.pttState;
  }, [state.pttState]);
  
  useEffect(() => {
    userRef.current = state.user;
  }, [state.user]);
  
  // Keep talkgroup settings refs in sync with state
  useEffect(() => {
    talkgroupVolumesRef.current = state.talkgroupVolumes;
  }, [state.talkgroupVolumes]);
  
  useEffect(() => {
    mutedTalkgroupsRef.current = state.mutedTalkgroups;
  }, [state.mutedTalkgroups]);
  
  useEffect(() => {
    selectedOutputDeviceRef.current = state.selectedOutputDevice;
  }, [state.selectedOutputDevice]);
  
  // Listen for device changes to keep device lists fresh
  useEffect(() => {
    const onChange = () => enumerateAudioDevices();
    navigator.mediaDevices?.addEventListener?.('devicechange', onChange);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', onChange);
  }, []);
  
  // TX FX processing refs
  const fxCtxRef = useRef<AudioContext | null>(null);
  const fxNodesRef = useRef<{ 
    source?: MediaStreamAudioSourceNode; 
    preFilter?: BiquadFilterNode; 
    comp?: DynamicsCompressorNode; 
    quantizer?: ScriptProcessorNode; 
    spectralShaper?: BiquadFilterNode; 
    hfRolloff?: BiquadFilterNode;
    noiseGenerator?: ScriptProcessorNode; 
    frameProcessor?: ScriptProcessorNode; 
    shaper?: WaveShaperNode; 
    gainNode?: GainNode; 
    postFilter?: BiquadFilterNode; 
    dest?: MediaStreamAudioDestinationNode 
  } | null>(null);

  // ---------- TX FX Processing Functions (INTELLIGIBLE NARROWBAND) ----------
  const buildTxProcessedStream = async (raw: MediaStream): Promise<MediaStream> => {
    await resumeAllAudioContexts();
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    const ctx = new Ctx();
    fxCtxRef.current = ctx;

    const source = ctx.createMediaStreamSource(raw);

    // --- Filters: 300–2900 Hz band, slightly steeper by cascading 2 poles each ---
    const hp1 = ctx.createBiquadFilter(); hp1.type = 'highpass'; hp1.frequency.value = 380;  hp1.Q.value = 0.707;
    const hp2 = ctx.createBiquadFilter(); hp2.type = 'highpass'; hp2.frequency.value = 380;  hp2.Q.value = 0.707;

    const lp1 = ctx.createBiquadFilter(); lp1.type = 'lowpass';  lp1.frequency.value = 2800; lp1.Q.value = 0.707;
    const lp2 = ctx.createBiquadFilter(); lp2.type = 'lowpass';  lp2.frequency.value = 2800; lp2.Q.value = 0.707;

    // Small presence bump around 2 kHz
    const presence = ctx.createBiquadFilter();
    presence.type = 'peaking';
    presence.frequency.value = 2000;  // 1.8–2.2 kHz is good
    presence.Q.value = 1.0;
    presence.gain.value = 3;          // try 2..4 dB

    // Gentle compressor (keeps consonants forward, avoids pumping)
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -20;   // start compressing earlier
    comp.knee.value = 12;
    comp.ratio.value = 3;         // 2.5–3.5
    comp.attack.value = 0.003;    // fast
    comp.release.value = 0.12;    // medium

    // Soft limiter via WaveShaper
    const shaper = ctx.createWaveShaper();
    const curve = new Float32Array(256);
    for (let i = 0; i < curve.length; i++) {
      const x = (i / (curve.length - 1)) * 2 - 1;
      curve[i] = Math.tanh(2.2 * x);  // gentle ceiling without fizz
    }
    shaper.curve = curve;
    shaper.oversample = '2x';

    // Output trim to prevent pushing WebRTC encoder too hard
    const outGain = ctx.createGain();
    outGain.gain.value = 0.85;

    const dest = ctx.createMediaStreamDestination();

    // Chain: source -> HP -> HP -> presence -> LP -> LP -> comp -> shaper -> outGain -> dest
    source.connect(hp1);
    hp1.connect(hp2);
    hp2.connect(presence);
    presence.connect(lp1);
    lp1.connect(lp2);
    lp2.connect(comp);
    comp.connect(shaper);
    shaper.connect(outGain);
    outGain.connect(dest);

    fxNodesRef.current = { source, dest, /* keep references if you want live tuning */ };
    return dest.stream;
  };

  const destroyTxProcessedStream = () => {
    try {
      fxNodesRef.current = null;
      fxCtxRef.current?.close();
    } catch {}
    fxCtxRef.current = null;
  };

  // ---------- Mount / Hard cleanup ----------
  useEffect(() => {
    mounts += 1;
    return () => {
      mounts -= 1;
      if (mounts <= 0) {
        hardCleanup();
      }
    };
  }, []);

  const hardCleanup = () => {
    // Close all consumers & audio
    try {
      consumersByProducer.forEach((c) => {
        try {
          c.close();
        } catch {}
      });
      consumersByProducer.clear();
    } catch {}
    try {
      audioByProducer.forEach((a) => {
        try {
          a.pause();
          (a as any).srcObject = null;
        } catch {}
      });
      audioByProducer.clear();
    } catch {}
    metaByProducer.clear();

    // Transports
    try {
      sendTransportSingleton?.close();
    } catch {}
    sendTransportSingleton = null;

    try {
      recvTransportsByTG.forEach((t) => t.close());
      recvTransportsByTG.clear();
    } catch {}

    // Socket
    try {
      sharedSocket?.disconnect();
    } catch {}
    sharedSocket = null;

    // Device
    sharedDevice = null;

    // TX FX cleanup
    destroyTxProcessedStream();

    // Audio recording cleanup
    try {
      audioRecordersRef.current.forEach((rec) => {
        if (rec.state === 'recording') rec.stop();
      });
    } catch {}
    audioRecordersRef.current.clear();
    audioChunksRef.current.clear();
    stoppedSet.current.clear();
    activeLogRef.current.clear();
    pendingLogRef.current.clear();

    // misc
    creatingRecvFor.clear();
    subscribingTGs.clear();
    currentUserId = null;
  };

  // ---------- Initialize ----------
  const initialize = useCallback(async (radioToken: string) => {
    try {
      setState((p) => ({ ...p, error: null }));

      if (!sharedSocket) {
        // Create radio socket with token authentication
        sharedSocket = io(SOCKET_URL + '/radio', {
          path: SOCKET_PATH,
          transports: ['websocket'],
          withCredentials: false,
          autoConnect: true,
          auth: {
            token: radioToken
          }
        });

        sharedSocket.on('connect', () => {
          setState((p) => ({ ...p, isConnected: true }));
        });

        sharedSocket.on('disconnect', () => {
          setState((p) => ({ ...p, isConnected: false }));
        });

        sharedSocket.on('auth:ok', (data) => {
          const uid = data?.user?.sub || data?.user?.id || data?.user?.userId || data?.user?.username;
          currentUserId = uid || null;
          const normalizedUser = { ...data.user, sub: uid, id: uid };
          setState((p) => ({ ...p, user: normalizedUser }));
        });

        sharedSocket.on('auth:error', (err) => {
          setState((p) => ({ ...p, error: err?.message || 'Authentication failed' }));
        });

        // TG activity lamps
        sharedSocket.on('tg:active', (payload: { talkgroupId: string; speaker?: AudioTalker; speakers?: AudioTalker[]; startedAt?: number }) => {
          setState((p) => {
            const next = new Map(p.activeTalkgroups);
            const speakers = payload.speakers || (payload.speaker ? [payload.speaker] : []);
            next.set(payload.talkgroupId, speakers);
            return { ...p, activeTalkgroups: next };
          });

          const speakers = payload.speakers || (payload.speaker ? [payload.speaker] : []);
          if (!speakers.length) return;

          const s = speakers[0];
          const me = currentUserId || userRef.current?.sub || userRef.current?.id;

          // Use composite key for overlapping talkers (fallback to userId if producerId missing)
          const compositeKey = keyFor(payload.talkgroupId, s.producerId ?? `user:${s.userId}`);
          
        // If no log exists yet for this composite key, create a provisional one now
        if (!activeLogRef.current.get(compositeKey)) {
          // Use normalized display name
          const displayName = normalizeName(s.displayName || s.userId);
          const logId = addTransmissionLog(
            payload.talkgroupId,
            s.userId || 'unknown',
            displayName,
            s.userId === me ? 'dispatcher' : 'unit'
          );
          activeLogRef.current.set(compositeKey, logId);
          pendingLogRef.current.set(compositeKey, logId);
          console.log('Created provisional transmission log in tg:active:', logId, 'User:', s.userId, 'DisplayName:', displayName, 'Key:', compositeKey);
        }
        });

        sharedSocket.on('tg:inactive', (payload: { talkgroupId: string }) => {
          // Debounce to tolerate brief producer gaps
          setTimeout(() => {
            // Use ref to get current PTT state (avoid stale closure)
            const isTx = pttStateRef.current.isTransmitting && pttStateRef.current.talkgroupId === payload.talkgroupId;
            
            // Check if there are any live consumers for this talkgroup
            const stillActive = Array.from(metaByProducer.entries()).some(([pid, m]) => (
              m.talkgroupId === payload.talkgroupId &&
              consumersByProducer.get(pid) &&
              !consumersByProducer.get(pid)!.closed
            ));
            
            console.log('[tg:inactive] isTx?', pttStateRef.current, 'stillActive?', stillActive);
            
            // Don't end anything while we're TX or audio is still flowing
            if (isTx || stillActive) return;
            
            setState((p) => {
              const next = new Map(p.activeTalkgroups);
              next.set(payload.talkgroupId, []);
              return { ...p, activeTalkgroups: next };
            });

            // End all active logs for this talkgroup (handle overlapping talkers)
            for (const [k, logId] of activeLogRef.current) {
              if (k.startsWith(`${payload.talkgroupId}::`) || k === payload.talkgroupId) {
                endTransmissionLog(payload.talkgroupId, logId, 'tg:inactive');
                activeLogRef.current.delete(k);
                pendingLogRef.current.delete(k);
              }
            }
            
          }, 250); // Small debounce
        });

        // Producer lifecycle
        sharedSocket.on('webrtc:newProducer', async (data: { producerId: string; talkgroupId: string; userId: string; kind: 'audio' | 'video' }) => {
          // Skip our own
          const me = currentUserId || userRef.current?.sub || userRef.current?.id;
          if (data.userId && me && data.userId === me) return;

          if (!sharedDevice) return;

          try {
            const transport = await ensureRecvTransport(data.talkgroupId);
            // Ask server to create server-side consumer
            sharedSocket!.emit(
              'webrtc:consume',
              {
                transportId: transport.id,
                producerId: data.producerId,
                rtpCapabilities: sharedDevice!.rtpCapabilities,
              },
              async (resp: any) => {
                // Accept both success:true and “implicit success” w/ consumerId
                const ok = resp?.success === true || (!!resp?.consumerId && !resp?.error);
                if (!ok) {
                  if (resp?.error === 'CANNOT_CONSUME') {
                    // Typically because DTLS still connecting; fast retry once
                    setTimeout(() => {
                      sharedSocket!.emit(
                        'webrtc:consume',
                        {
                          transportId: transport.id,
                          producerId: data.producerId,
                          rtpCapabilities: sharedDevice!.rtpCapabilities,
                        },
                        async (retry: any) => {
                          const ok2 = retry?.success === true || (!!retry?.consumerId && !retry?.error);
                          if (!ok2) return;
                          await attachConsumer(transport, retry, data);
                        }
                      );
                    }, 120);
                    return;
                  }
                  return;
                }
                await attachConsumer(transport, resp, data);
              }
            );
          } catch {
            // swallow
          }
        });

        sharedSocket.on('webrtc:producerClosed', ({ producerId }: { producerId: string }) => {
          // Close consumer and clean up audio element
          const c = consumersByProducer.get(producerId);
          if (c) {
            try {
              c.close();
            } catch {}
            consumersByProducer.delete(producerId);
          }
          const a = audioByProducer.get(producerId);
          if (a) {
            try {
              a.pause();
              (a as any).srcObject = null;
            } catch {}
            audioByProducer.delete(producerId);
          }
          
          // End the associated transmission log
          const meta = metaByProducer.get(producerId);
          if (meta) {
            const k = keyFor(meta.talkgroupId, producerId);
            const logId = activeLogRef.current.get(k);
            if (logId) {
              endTransmissionLog(meta.talkgroupId, logId, 'producerClosed');
              activeLogRef.current.delete(k);
              pendingLogRef.current.delete(k);
              console.log('Ended transmission log on producer closed:', logId, 'Key:', k);
            }
          }
          
          metaByProducer.delete(producerId);
        });

        // PTT status
        sharedSocket.on('ptt:denied', () => playTone('busy'));
        sharedSocket.on('ptt:preempted', () => {
          setState((p) => ({
            ...p,
            pttState: { isTransmitting: false, talkgroupId: null, startTime: null },
          }));
          playTone('preempt');
        });

        // Talkgroup subscription management (for channel changes)
        sharedSocket.on('tg:switchChannel', async ({ talkgroupId, previousTalkgroups }: { talkgroupId: string; previousTalkgroups: string[] }) => {
          console.log(`[tg:switchChannel] Switched to channel: ${talkgroupId} (from: ${previousTalkgroups.join(', ')})`);
          try {
            // Unsubscribe from all previous channels
            for (const prevTg of previousTalkgroups) {
              await unsubscribeFromTalkgroup(prevTg);
            }
            
            // Subscribe to the new channel
            await subscribeToTalkgroup(talkgroupId);
            console.log(`[tg:switchChannel] Successfully switched to ${talkgroupId}`);
            
            // Update the UI to reflect the new channel
            subscriptionCallbacks?.setCurrentUserChannel?.(talkgroupId);
          } catch (error) {
            console.error(`[tg:switchChannel] Failed to switch to ${talkgroupId}:`, error);
          }
        });
      }

      socketRef.current = sharedSocket;

      // Device
      if (!sharedDevice) {
        const caps = await getRouterCaps(serverUrl, radioToken);
        sharedDevice = new mediasoupClient.Device();
        await sharedDevice.load({ routerRtpCapabilities: caps });
      }
      deviceRef.current = sharedDevice;

      // enumerate devices
      await enumerateAudioDevices();

      // Prewarm recv transports (no-op errors allowed)
      const tgs = (state.talkgroups || [])
        .filter(Boolean)
        .map((tg: any) => tg.id)
        .filter((id: string) => !!id);
      for (const tg of tgs) {
        ensureRecvTransport(tg).catch(() => {});
      }

      setState((p) => ({ ...p, isInitialized: true }));
    } catch (err: any) {
      setState((p) => ({ ...p, error: err?.message || 'Initialization failed' }));
    }
  }, [serverUrl, state.talkgroups]);

  // ---------- Router caps ----------
  const getRouterCaps = async (serverUrl: string, token?: string) => {
    if (routerCapsCache) return routerCapsCache;
    if (routerCapsPromise) return routerCapsPromise;

    routerCapsPromise = (async () => {
      const res = await fetch(`${serverUrl}/api/radio/config`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`config fetch failed: ${res.status}`);
      const data = await res.json();
      routerCapsCache = data.routerRtpCapabilities;
      iceServersCache = data.iceServers || [];
      return routerCapsCache;
    })();

    const out = await routerCapsPromise;
    routerCapsPromise = null;
    return out;
  };

  // ---------- Media devices ----------
  const enumerateAudioDevices = async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const audio = list
        .filter((d) => d.kind === 'audioinput' || d.kind === 'audiooutput')
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `${d.kind} ${d.deviceId.slice(0, 8)}`,
          kind: d.kind,
        }));
      setState((p) => ({ ...p, devices: audio }));
      const defaultIn = audio.find((d) => d.kind === 'audioinput');
      const defaultOut = audio.find((d) => d.kind === 'audiooutput');
      setState((p) => ({
        ...p,
        selectedInputDevice: p.selectedInputDevice || defaultIn?.deviceId || null,
        selectedOutputDevice: p.selectedOutputDevice || defaultOut?.deviceId || null,
      }));
    } catch {
      // ignore
    }
  };

  // ---------- Transports ----------
  const ensureRecvTransport = async (talkgroupId: string) => {
    const existing = recvTransportsByTG.get(talkgroupId);
    if (existing) return existing;
    if (creatingRecvFor.has(talkgroupId)) {
      // spin wait briefly
      while (creatingRecvFor.has(talkgroupId) && !recvTransportsByTG.get(talkgroupId)) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 10));
      }
      return recvTransportsByTG.get(talkgroupId)!;
    }

    if (!socketRef.current || !deviceRef.current) throw new Error('Not initialized');
    
    creatingRecvFor.add(talkgroupId);
    try {
      const transportInfo: any = await new Promise((resolve, reject) => {
        socketRef.current!.emit('webrtc:createRecvTransport', { talkgroupId }, (resp: any) => {
          if (resp?.success) resolve(resp);
          else reject(new Error(resp?.error || 'createRecvTransport failed'));
        });
      });

      const t = deviceRef.current.createRecvTransport({
        id: transportInfo.id,
        iceParameters: transportInfo.iceParameters,
        iceCandidates: transportInfo.iceCandidates,
        dtlsParameters: transportInfo.dtlsParameters,
        iceServers: iceServersCache,
      });

      t.on('connect', ({ dtlsParameters }, cb, eb) => {
        socketRef.current!.emit(
          'webrtc:connectTransport',
          { transportId: t.id, dtlsParameters },
          (resp: any) => (resp?.success ? cb() : eb(new Error(resp?.error || 'connectTransport failed')))
        );
      });

      t.on('connectionstatechange', (st) => {
        console.log(`[RecvTransport] Connection state changed for ${talkgroupId}: ${st}`);
        if (st === 'failed') {
          console.error(`[RecvTransport] Transport failed for ${talkgroupId}`, t.id);
        } else if (st === 'connected') {
          console.log(`[RecvTransport] Transport connected successfully for ${talkgroupId}`);
        }
      });

      recvTransportsByTG.set(talkgroupId, t);
      return t;
    } finally {
      creatingRecvFor.delete(talkgroupId);
    }
  };

  const ensureSendTransport = async (talkgroupIdForTicket: string) => {
    if (sendTransportSingleton) return sendTransportSingleton;
    if (!socketRef.current || !deviceRef.current) throw new Error('Not initialized');

    const info: any = await new Promise((resolve, reject) => {
      socketRef.current!.emit('webrtc:createSendTransport', { talkgroupId: talkgroupIdForTicket }, (resp: any) => {
        if (resp?.success) resolve(resp);
        else reject(new Error(resp?.error || 'createSendTransport failed'));
      });
    });

    const t = deviceRef.current.createSendTransport({
      id: info.id,
      iceParameters: info.iceParameters,
      iceCandidates: info.iceCandidates,
      dtlsParameters: info.dtlsParameters,
      iceServers: iceServersCache,
    });

    t.on('connect', ({ dtlsParameters }, cb, eb) => {
      socketRef.current!.emit(
        'webrtc:connectTransport',
        { transportId: t.id, dtlsParameters },
        (resp: any) => (resp?.success ? cb() : eb(new Error(resp?.error || 'connectTransport failed')))
      );
    });

    t.on('produce', ({ kind, rtpParameters, appData }, cb, eb) => {
      socketRef.current!.emit(
        'webrtc:produce',
        { transportId: t.id, kind, rtpParameters, appData },
        (resp: any) => (resp?.success ? cb({ id: resp.producerId }) : eb(new Error(resp?.error || 'produce failed')))
      );
    });

    // Add connection state logging for debugging NAT/ICE issues
    t.on('connectionstatechange', (st) => {
      if (st === 'failed' || st === 'disconnected') {
        console.warn('Send transport connection issue:', st, 'Transport ID:', t.id);
      }
    });

    sendTransportSingleton = t;
    return t;
  };

  // ---------- Attach consumer & audio ----------
  const attachConsumer = async (
    transport: mediasoupClient.types.Transport,
    consumeResp: any,
    producerMeta: { producerId: string; talkgroupId: string; userId: string; kind: 'audio' | 'video' }
  ) => {
    const consumer = await transport.consume({
      id: consumeResp.consumerId,
      producerId: consumeResp.producerId,
      kind: consumeResp.kind,
      rtpParameters: consumeResp.rtpParameters,
    });

    consumersByProducer.set(consumeResp.producerId, consumer);
    metaByProducer.set(consumeResp.producerId, {
      talkgroupId: producerMeta.talkgroupId,
      userId: producerMeta.userId,
      kind: consumer.kind,
    });

    if (consumer.kind === 'audio') {
      const el = new Audio();
      el.autoplay = true;
      el.preload = 'none';
      
      // Apply current talkgroup settings - use refs to avoid stale state
      const currentVolume = talkgroupVolumesRef.current.get(producerMeta.talkgroupId) ?? 80;
      const isMuted = mutedTalkgroupsRef.current.has(producerMeta.talkgroupId);
      
      console.log(`[attachConsumer] Setting up audio for talkgroup: ${producerMeta.talkgroupId}, producer: ${producerMeta.userId}`);
      console.log(`[attachConsumer] Volume: ${currentVolume}, Muted: ${isMuted}`);
      
      el.volume = Math.max(0, Math.min(1, currentVolume / 100));
      el.muted = isMuted;
      
      // Add detailed event listeners for debugging
      el.addEventListener('play', () => {
        console.log(`[Audio] Playing audio from ${producerMeta.userId} on ${producerMeta.talkgroupId}`);
      });
      el.addEventListener('pause', () => {
        console.log(`[Audio] Paused audio from ${producerMeta.userId} on ${producerMeta.talkgroupId}`);
      });
      el.addEventListener('error', (e) => {
        console.error(`[Audio] Error playing audio from ${producerMeta.userId}:`, e, el.error);
      });
      el.addEventListener('loadedmetadata', () => {
        console.log(`[Audio] Metadata loaded for ${producerMeta.userId}`);
      });
      el.addEventListener('canplay', () => {
        console.log(`[Audio] Can play audio from ${producerMeta.userId}`);
      });
      
      const audioStream = new MediaStream([consumer.track]);
      (el as any).srcObject = audioStream;
      console.log(`[attachConsumer] Audio stream attached, track state: ${consumer.track.readyState}, enabled: ${consumer.track.enabled}`);

      // set sink if possible
      const outId = selectedOutputDeviceRef.current;
      if (outId && (el as any).setSinkId) {
        try {
          await (el as any).setSinkId(outId);
        } catch {
          // ignore
        }
      }
      audioByProducer.set(consumeResp.producerId, el);
      tryPlay(el);

      // Start recording the incoming audio stream for transmission logs
      // Skip recording our own transmissions (they're already recorded in startTransmitting)
      const me = currentUserId || userRef.current?.sub || userRef.current?.id;
      const isSelf = producerMeta.userId && me && producerMeta.userId === me;
      
      if (!isSelf) {
        // Look for existing log and rebind if needed
        const tgId = producerMeta.talkgroupId;
        const prodKey = keyFor(tgId, consumeResp.producerId);
        const userKey = keyFor(tgId, `user:${producerMeta.userId}`);

        // Get normalized display name for consistent naming
        const displayName = normalizeName((producerMeta as any).displayName || producerMeta.userId);

        // Prefer an existing log (producer key). If none, try to adopt the user-scoped provisional log.
        let logId =
          activeLogRef.current.get(prodKey) ||
          pendingLogRef.current.get(prodKey) ||
          activeLogRef.current.get(userKey) ||
          pendingLogRef.current.get(userKey);

        if (logId) {
          // Re-bind that provisional/user log to the real producer key
          activeLogRef.current.set(prodKey, logId);
          pendingLogRef.current.delete(prodKey);    // just in case
          // Clean up the user-key mappings so we don't render duplicates
          activeLogRef.current.delete(userKey);
          pendingLogRef.current.delete(userKey);
          console.log('Rebound existing log to producer key:', logId, 'From:', userKey, 'To:', prodKey);
        } else {
          // No prior log — create one now
          logId = addTransmissionLog(tgId, producerMeta.userId || 'unknown', displayName, 'unit');
          activeLogRef.current.set(prodKey, logId);
          pendingLogRef.current.set(prodKey, logId);
          console.log('Created new transmission log in attachConsumer:', logId, 'User:', producerMeta.userId, 'Key:', prodKey);
        }

        // Start recording the stream for this log
        let mimeType = 'audio/webm';
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/wav')) {
          mimeType = 'audio/wav';
        }

        const logData = {
          talkgroupId: producerMeta.talkgroupId,
          displayName: displayName,
          type: 'unit',
          startTime: Date.now(),
          mimeType
        };

        console.log('Starting unit audio recording for:', logId, 'User:', producerMeta.userId, 'Key:', prodKey);
        startAudioRecording(logId, audioStream, logData);
        
        // Set up consumer-side event handlers to end logs
        const endFor = (reason: string) => {
          const k = keyFor(producerMeta.talkgroupId, consumeResp.producerId);
          const logId = activeLogRef.current.get(k);
          if (logId) {
            endTransmissionLog(producerMeta.talkgroupId, logId, reason);
            activeLogRef.current.delete(k);
            pendingLogRef.current.delete(k);
            console.log('Ended transmission log on consumer event:', logId, 'Reason:', reason);
          }
        };

        // Set up consumer event handlers to end logs
        (consumer as any).on('producerclose', () => {
          // End log
          endFor('consumer:producerclose');

          // Cleanup consumer & audio element defensively
          try { consumer.close(); } catch {}
          consumersByProducer.delete(consumeResp.producerId);

          const el = audioByProducer.get(consumeResp.producerId);
          if (el) {
            try { el.pause(); (el as any).srcObject = null; } catch {}
            audioByProducer.delete(consumeResp.producerId);
          }

          metaByProducer.delete(consumeResp.producerId);
        });

        consumer.on('transportclose', () => endFor('consumer:transportclose'));
        consumer.on('trackended', () => endFor('consumer:trackended'));
        consumer.on('@close', () => endFor('consumer:close'));
      }
    }
  };

  const getAudioElsForTG = (talkgroupId: string) => {
    const out: HTMLAudioElement[] = [];
    metaByProducer.forEach((meta, prodId) => {
      if (meta.talkgroupId === talkgroupId) {
        const a = audioByProducer.get(prodId);
        if (a) out.push(a);
      }
    });
    return out;
  };


  // ---------- Public API ----------
  const subscribeToTalkgroup = useCallback(async (talkgroupId: string) => {
    if (!socketRef.current) return;
    if (subscribingTGs.has(talkgroupId)) return;
    subscribingTGs.add(talkgroupId);

    try {
      await resumeAllAudioContexts();
      const transport = await ensureRecvTransport(talkgroupId);

      socketRef.current.emit('tg:subscribe', { talkgroupId });
      
      // Update UI to reflect the new channel selection
      subscriptionCallbacks?.setCurrentUserChannel?.(talkgroupId);

      // backfill any current producers
      socketRef.current.emit('webrtc:getProducers', { talkgroupId }, async (resp: any) => {
        if (!resp?.success || !resp?.producers) return;
        for (const p of resp.producers as Array<{ producerId: string; userId: string; kind: 'audio' | 'video' }>) {
          // Skip our own producer
          const me = currentUserId || userRef.current?.sub || userRef.current?.id;
          if (p.userId && me && p.userId === me) continue;
          if (consumersByProducer.has(p.producerId)) continue;

          socketRef.current!.emit(
            'webrtc:consume',
            {
              transportId: transport.id,
              producerId: p.producerId,
              rtpCapabilities: deviceRef.current!.rtpCapabilities,
            },
            async (cResp: any) => {
              const ok = cResp?.success === true || (!!cResp?.consumerId && !cResp?.error);
              if (!ok) {
                if (cResp?.error === 'CANNOT_CONSUME') return;
                return;
              }
              await attachConsumer(transport, cResp, { producerId: p.producerId, talkgroupId, userId: p.userId, kind: p.kind });
            }
          );
        }
      });
    } catch {
      // ignore
    } finally {
      subscribingTGs.delete(talkgroupId);
    }
  }, [state.user?.sub, state.user?.id, subscriptionCallbacks]);

  const unsubscribeFromTalkgroup = async (talkgroupId: string) => {
    if (!socketRef.current) return;
    try {
      socketRef.current.emit('tg:unsubscribe', { talkgroupId });
      // Close and purge any consumers & audio tied to this TG
      metaByProducer.forEach((meta, pid) => {
        if (meta.talkgroupId === talkgroupId) {
          const c = consumersByProducer.get(pid);
          if (c) {
            try {
              c.close();
            } catch {}
            consumersByProducer.delete(pid);
          }
          const a = audioByProducer.get(pid);
          if (a) {
            try {
              a.pause();
              (a as any).srcObject = null;
            } catch {}
            audioByProducer.delete(pid);
          }
          metaByProducer.delete(pid);
        }
      });
      
      // Clear active logs for this talkgroup
      for (const [k, logId] of Array.from(activeLogRef.current.entries())) {
        if (k.startsWith(`${talkgroupId}::`) || k === talkgroupId) {
          endTransmissionLog(talkgroupId, logId, 'unsubscribeFromTalkgroup');
          activeLogRef.current.delete(k);
          pendingLogRef.current.delete(k);
        }
      }
    } catch {
      // ignore
    }
  };

  const startTransmitting = async (talkgroupId: string) => {
    if (!socketRef.current || state.pttState.isTransmitting) return;

    await resumeAllAudioContexts();

    // All-call special
    if (talkgroupId === 'ALL_CALL') {
      socketRef.current.emit('ptt:start', { talkgroupId: 'ALL_CALL' }, async (resp: any) => {
        if (!resp?.granted) {
          if (resp?.reason === 'busy') playTone('busy');
          return;
        }

        // Single mic stream for all producers
        const constraints: MediaStreamConstraints = {
          audio: {
            deviceId: state.selectedInputDevice ? { exact: state.selectedInputDevice } : undefined,
            channelCount: 1,
            sampleRate: 48000,
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        micStreamRef.current = stream;

        // Apply TX FX if enabled
        let txStream = stream;
        if (state.txFxEnabled) {
          txStream = await buildTxProcessedStream(stream);
          // Record processed TX audio for analysis (10 seconds)
          recordStream(txStream, 10000);
        }

        const producers = new Map<string, mediasoupClient.types.Producer>();
        const t = await ensureSendTransport('ALL_CALL');

        // create one producer per non-all-call TG
        const list = (state.talkgroups || []).filter((tg: any) => !tg.isAllCall);
        const baseTrack = txStream.getAudioTracks()[0];
        for (const tg of list) {
          try {
            // Clone track for each TG (can't reuse same track on same transport)
            const trackForTg = tg === list[0] ? baseTrack : baseTrack.clone();
            const prod = await t.produce({
              track: trackForTg,
              appData: { talkgroupId: tg.id },
            });
            producers.set(tg.id, prod);
          } catch (e) {
            // optional: console.warn('ALL_CALL produce failed for', tg.id, e);
          }
        }

        setState((p) => ({
          ...p,
          pttState: { isTransmitting: true, talkgroupId: 'ALL_CALL', startTime: Date.now(), allCallProducers: producers },
        }));

        // Log dispatcher's ALL_CALL transmission with audio recording
        const logId = addTransmissionLog(
          'ALL_CALL',
          currentUserId || state.user?.sub || state.user?.id || 'dispatcher',
          state.user?.displayName || 'Dispatcher',
          'dispatcher',
          txStream // Record the processed audio stream
        );
        // Use composite key for dispatcher logs too
        const dispatcherKey = keyFor('ALL_CALL', 'dispatcher');
        activeLogRef.current.set(dispatcherKey, logId);
        pendingLogRef.current.set(dispatcherKey, logId);

        playTone('start');
      });

      return;
    }

    // Single TG
    socketRef.current.emit('ptt:start', { talkgroupId }, async (resp: any) => {
      if (!resp?.granted) {
        if (resp?.reason === 'busy') playTone('busy');
        return;
      }

      // Units get talk-permit beep (consoles shouldn't)
      if (state.user?.role === 'unit') playTalkPermitSound();

      const constraints: MediaStreamConstraints = {
        audio: {
          deviceId: state.selectedInputDevice ? { exact: state.selectedInputDevice } : undefined,
          channelCount: 1,
          sampleRate: 48000,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      micStreamRef.current = stream;

      // Apply TX FX if enabled
      let txStream = stream;
      if (state.txFxEnabled) {
        txStream = await buildTxProcessedStream(stream);
        // Record processed TX audio for analysis (10 seconds)
        recordStream(txStream, 10000);
      }

      const t = await ensureSendTransport(talkgroupId);
      const prod = await t.produce({ track: txStream.getAudioTracks()[0], appData: { talkgroupId } });
      singleProducerRef.current = prod;

      setState((p) => ({
        ...p,
        pttState: { isTransmitting: true, talkgroupId, startTime: Date.now() },
      }));

      // Log dispatcher's transmission with audio recording
      const logId = addTransmissionLog(
        talkgroupId,
        currentUserId || state.user?.sub || state.user?.id || 'dispatcher',
        state.user?.displayName || 'Dispatcher',
        'dispatcher',
        txStream // Record the processed audio stream
      );
      // Use composite key for dispatcher logs too
      const dispatcherKey = keyFor(talkgroupId, 'dispatcher');
      activeLogRef.current.set(dispatcherKey, logId);
      pendingLogRef.current.set(dispatcherKey, logId);

      playTone('start');
    });
  };

  const stopTransmitting = async () => {
    if (!socketRef.current || !state.pttState.isTransmitting) return;

    try {
      // Add squelch tail (brief noise burst) if TX FX is enabled
      if (state.txFxEnabled && fxCtxRef.current) {
        try {
          const ctx = fxCtxRef.current;
          const noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * 0.1, ctx.sampleRate); // 100ms
          const noiseData = noiseBuffer.getChannelData(0);
          for (let i = 0; i < noiseData.length; i++) {
            noiseData[i] = (Math.random() - 0.5) * 0.1; // Squelch tail noise
          }
          
          const noiseSource = ctx.createBufferSource();
          const gainNode = ctx.createGain();
          noiseSource.buffer = noiseBuffer;
          noiseSource.connect(gainNode);
          gainNode.connect(ctx.destination);
          
          gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
          
          noiseSource.start();
        } catch (e) {
          // Ignore squelch tail errors
        }
      }

      const tg = state.pttState.talkgroupId;
      
      // End dispatcher's transmission log BEFORE stopping mic tracks
      if (tg) {
        // Use composite key for dispatcher logs
        const dispatcherKey = keyFor(tg, 'dispatcher');
        const logId = activeLogRef.current.get(dispatcherKey);
        if (logId) {
          const transmissionDuration = Date.now() - (state.pttState.startTime || Date.now());
          console.log('Ending transmission log:', logId, 'Duration:', transmissionDuration, 'ms');
          endTransmissionLog(tg, logId, 'stopTransmitting');
          activeLogRef.current.delete(dispatcherKey);
          pendingLogRef.current.delete(dispatcherKey);
        }
      }

      // Close producers
      if (state.pttState.talkgroupId === 'ALL_CALL' && state.pttState.allCallProducers) {
        for (const [, prod] of state.pttState.allCallProducers) {
          try {
            prod.close();
          } catch {}
        }
        state.pttState.allCallProducers.clear();
      } else if (singleProducerRef.current) {
        try {
          singleProducerRef.current.close();
        } catch {}
        singleProducerRef.current = null;
      }

      // Stop mic AFTER ending transmission log
      if (micStreamRef.current) {
        try {
          micStreamRef.current.getTracks().forEach((t) => t.stop());
        } catch {}
        micStreamRef.current = null;
      }

      // Clean up TX FX processing
      destroyTxProcessedStream();

      socketRef.current.emit('ptt:stop', { talkgroupId: tg }, () => {
        setState((p) => ({
          ...p,
          pttState: { isTransmitting: false, talkgroupId: null, startTime: null, allCallProducers: null },
        }));
        playTone('end');
      });
    } catch {
      // ignore
    }
  };

  const updateDevice = (type: 'input' | 'output', deviceId: string) => {
    setState((p) => ({
      ...p,
      [type === 'input' ? 'selectedInputDevice' : 'selectedOutputDevice']: deviceId,
    }));

    // Inform server (optional)
    socketRef.current?.emit('device:update', { [`${type}Id`]: deviceId });

    // For outputs, update all audio elements
    if (type === 'output') {
      for (const el of audioByProducer.values()) {
        if ((el as any).setSinkId) {
          (el as any).setSinkId(deviceId).catch(() => {});
        }
      }
    }
  };

  const setAudioVolume = (producerId: string, volume: number) => {
    const el = audioByProducer.get(producerId);
    if (el) el.volume = Math.max(0, Math.min(1, volume / 100));
  };

  const setAudioMuted = (producerId: string, muted: boolean) => {
    const el = audioByProducer.get(producerId);
    if (el) el.muted = !!muted;
  };

  const setTalkgroupVolume = (talkgroupId: string, volume: number) => {
    console.log(`[useWebRTC] Setting volume for ${talkgroupId} to ${volume}`);
    
    // Update state
    setState(prev => {
      const newVolumes = new Map(prev.talkgroupVolumes).set(talkgroupId, volume);
      console.log(`[useWebRTC] Updated talkgroupVolumes map:`, newVolumes);
      return {
        ...prev,
        talkgroupVolumes: newVolumes
      };
    });
    
    // Apply to existing audio elements
    const audioEls = getAudioElsForTG(talkgroupId);
    console.log(`[useWebRTC] Found ${audioEls.length} audio elements for ${talkgroupId}`);
    audioEls.forEach((el) => {
      el.volume = Math.max(0, Math.min(1, volume / 100));
      console.log(`[useWebRTC] Set audio element volume to ${el.volume}`);
    });
  };

  const setTalkgroupMuted = (talkgroupId: string, muted: boolean) => {
    console.log(`[useWebRTC] Setting mute for ${talkgroupId} to ${muted}`);
    
    // Update state
    setState(prev => {
      const newMutedTalkgroups = new Set(prev.mutedTalkgroups);
      if (muted) {
        newMutedTalkgroups.add(talkgroupId);
      } else {
        newMutedTalkgroups.delete(talkgroupId);
      }
      console.log(`[useWebRTC] Updated mutedTalkgroups set:`, newMutedTalkgroups);
      return {
        ...prev,
        mutedTalkgroups: newMutedTalkgroups
      };
    });
    
    // Apply to existing audio elements
    const audioEls = getAudioElsForTG(talkgroupId);
    console.log(`[useWebRTC] Found ${audioEls.length} audio elements for ${talkgroupId}`);
    audioEls.forEach((el) => {
      el.muted = !!muted;
      console.log(`[useWebRTC] Set audio element muted to ${el.muted}`);
    });
  };

  const testAudioPlayback = () => {
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.value = 0.12;
      osc.frequency.value = 440;
      osc.start();
      osc.stop(ctx.currentTime + 0.1);
    } catch {}
  };

  const playTalkPermitSound = () => {
    try {
      const a = new Audio('/TalkPermit.mp3');
      a.volume = 0.8;
      a.play().catch(() => {});
    } catch {}
  };

  const playTone = (type: 'start' | 'end' | 'busy' | 'preempt') => {
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      const now = ctx.currentTime;
      const finish = (dur: number) => {
        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + dur);
        osc.start(now);
        osc.stop(now + dur);
      };

      if (type === 'start') {
        osc.frequency.setValueAtTime(800, now);
        finish(0.1);
      } else if (type === 'end') {
        osc.frequency.setValueAtTime(400, now);
        finish(0.2);
      } else if (type === 'busy') {
        osc.frequency.setValueAtTime(1200, now);
        finish(0.5);
      } else {
        osc.frequency.setValueAtTime(1000, now);
        finish(0.3);
      }
    } catch {}
  };

  const cleanup = useCallback(() => {
    try {
      singleProducerRef.current?.close();
    } catch {}
    singleProducerRef.current = null;
    try {
      micStreamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {}
    micStreamRef.current = null;
  }, []);

  // auto-init on mount if token present
  useEffect(() => {
    if (token && !state.isInitialized && !state.error) {
      initialize(token);
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ---------- Expose ----------
  return {
    ...state,
    initialize,
    startTransmitting,
    stopTransmitting,
    subscribeToTalkgroup,
    unsubscribeFromTalkgroup,
    updateDevice,
    testAudioPlayback,
    playTone,
    playTalkPermitSound,
    setAudioVolume,
    setAudioMuted,
    setTalkgroupVolume,
    setTalkgroupMuted,
    cleanup,
    // TX FX controls
    txFxEnabled: state.txFxEnabled,
    setTxFxEnabled,
    fxMode: state.fxMode,
    setFxMode,
    // P25 Vocoder controls
    p25VocoderConfig: state.p25VocoderConfig,
    setP25VocoderMode,
    setP25VocoderConfig,
    P25_PRESETS,
    // Transmission logging
    transmissionLogs: state.transmissionLogs,
    playTransmissionAudio,
    // optional: expose low-level creators (kept for API compatibility)
    createSendTransport: ensureSendTransport,
    createRecvTransport: ensureRecvTransport,
  };
};
